import React from 'react';
import PropTypes from 'prop-types';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import {SEARCH_TYPE} from '../search-prop-types';
import withConfig from 'config/withConfig';
import Button from '@spothero/ui/button';
import Card from 'common/card/Card';
import SpotListItem from './SpotListItem';
import SpotListItemPlaceholder from './SpotListItemPlaceholder';
import SpotListNoResults from './SpotListNoResults';
import {updateSelected} from 'spot/spot-actions';
import {cancelSpotRequests} from 'spots/spots-actions';
import {resetFilters} from 'filters/filters-actions';

const renderPlaceholders = (count = 5) => {
    const placeholders = [];
    let i = 0;

    for (i; i < count; i++) {
        placeholders.push(<SpotListItemPlaceholder key={i} />);
    }

    return <>{placeholders}</>;
};

export const SpotList = ({
    isMobile,
    searchType,
    spots,
    numActiveFilters,
    isMonthly,
    starts,
    ends,
    event,
    siteUrl,
    destination,
    city,
    urlDateFormat,
    showListLoader,
    showLoadingComplete,
    loadNextPage,
    onResetFiltersClick,
    updateSelectedSpot,
    cancelAllSpotRequests,
    isRebookingReservation,
    isAdmin,
    sortBy,
    selectedSpot,
    isEventListEnabled,
    onToggleEventList,
    isLowInventoryEnabled,
    isSoldOutEnabled,
    showNoSpotsFound,
    onShowDetailsClick,
}) => {
    const {
        monthly_phone_number: monthlyPhoneNumber,
        monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
    } = city;
    const hasDestination = !isEmpty(destination);
    const end = isMonthly
        ? moment(starts, moment.ISO_8601)
              .add(1, 'month')
              .format(urlDateFormat)
        : ends;
    const url =
        searchType === 'event' && !isEmpty(event)
            ? `${siteUrl}/${event.search_url}`
            : hasDestination
            ? `${siteUrl}/${city.slug}/${
                  isMonthly && destination.monthly_relative_url
                      ? destination.monthly_relative_url
                      : destination.relative_url
              }?starts=${starts}&ends=${ends}`
            : `${siteUrl}/${
                  isMonthly && city.monthly_relative_url
                      ? city.monthly_relative_url
                      : city.relative_url
              }?starts=${starts}&ends=${ends}`;

    return (
        <div
            className="SpotList"
            itemProp="offers"
            itemScope
            itemType="http://schema.org/AggregateOffer"
        >
            {url && <meta itemProp="url" content={url} />}
            {spots.length > 0 && (
                <>
                    <meta itemProp="validFrom" content={starts} />
                    <meta itemProp="validThrough" content={end} />
                </>
            )}
            <meta itemProp="offerCount" content={spots.length} />
            <div className="SpotList-spots">
                {isMobile && isEventListEnabled && (
                    <Card className="SpotList-event-cta">
                        <p>Going to an event at {destination.title}?</p>
                        <Button color="primary" onClick={onToggleEventList}>
                            View Upcoming Events
                        </Button>
                    </Card>
                )}
                {isMonthly && monthlyPhoneNumber && (
                    <Card className="SpotList-monthly-help">
                        <p>
                            Questions about monthly parking?{' '}
                            <span>
                                Give us a call at{' '}
                                <a href={`tel:${monthlyPhoneNumber}`}>
                                    {monthlyPhoneNumberFriendly}
                                </a>
                                .
                            </span>
                        </p>
                    </Card>
                )}
                {!isMobile &&
                    !showNoSpotsFound &&
                    spots.length < 1 &&
                    renderPlaceholders()}
                {showNoSpotsFound && (
                    <SpotListNoResults
                        numFilters={numActiveFilters}
                        onResetFiltersClick={onResetFiltersClick}
                    />
                )}
                {spots
                    .filter(({selectedRate}) => {
                        // ensure spots with either missing selectedRate
                        // or those without the proper selectedRate.rule_type are not offered to users
                        return (
                            selectedRate &&
                            ((isMonthly &&
                                selectedRate.rule_type === 'monthly') ||
                                (!isMonthly &&
                                    selectedRate.rule_type !== 'monthly'))
                        );
                    })
                    .map(spot => (
                        <SpotListItem
                            key={`${spot.spotId}-${spot.activeMarker}`}
                            spot={spot}
                            isMonthly={isMonthly}
                            isRebookingReservation={isRebookingReservation}
                            isAdmin={isAdmin}
                            updateSelectedSpot={updateSelectedSpot}
                            cancelAllSpotRequests={cancelAllSpotRequests}
                            currentlySelectedSpot={selectedSpot}
                            isLowInventoryEnabled={isLowInventoryEnabled}
                            isSoldOutEnabled={isSoldOutEnabled}
                            sortBy={sortBy}
                            onShowDetailsClick={onShowDetailsClick}
                        />
                    ))}
            </div>
            {isMobile && (
                <>
                    {!showLoadingComplete &&
                        !showNoSpotsFound &&
                        (!spots || spots.length === 0) &&
                        renderPlaceholders()}
                    {!showLoadingComplete && (spots && spots.length > 0) && (
                        <div className="SpotList-additional-actions">
                            <Button
                                color="primary"
                                loading={showListLoader}
                                onClick={loadNextPage}
                            >
                                {sortBy === 'price'
                                    ? 'Load More and Re-Sort'
                                    : 'Load More Spots'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

SpotList.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    numActiveFilters: PropTypes.number,
    isMonthly: PropTypes.bool.isRequired,
    showListLoader: PropTypes.bool,
    showLoadingComplete: PropTypes.bool,
    searchType: SEARCH_TYPE.isRequired,
    starts: PropTypes.string,
    ends: PropTypes.string,
    spots: PropTypes.arrayOf(PropTypes.object),
    siteUrl: PropTypes.string,
    urlDateFormat: PropTypes.string,
    loadNextPage: PropTypes.func,
    onResetFiltersClick: PropTypes.func,
    cancelAllSpotRequests: PropTypes.func.isRequired,
    updateSelectedSpot: PropTypes.func.isRequired,
    isRebookingReservation: PropTypes.bool,
    isAdmin: PropTypes.bool,
    sortBy: PropTypes.string.isRequired,
    event: PropTypes.object,
    destination: PropTypes.shape(DESTINATION_DATA).isRequired,
    city: PropTypes.object,
    selectedSpot: PropTypes.object,
    isEventListEnabled: PropTypes.bool,
    onToggleEventList: PropTypes.func,
    isLowInventoryEnabled: PropTypes.bool,
    isSoldOutEnabled: PropTypes.bool,
    showNoSpotsFound: PropTypes.bool,
    onShowDetailsClick: PropTypes.func,
};

const mapStateToProps = state => {
    const {
        search: {
            data: {searchType, sortBy, showNoSpotsFound},
        },
        searchRequest: {starts, ends, monthly: isMonthly},
        checkout: {isRebookingReservation},
        filters: {numActive: numActiveFilters},
        user: {
            data: {isAdmin},
        },
        city: {data: city},
        destination: {data: destination},
        event: {data: event},
        spot: {selected: selectedSpot},
    } = state;

    return {
        searchType,
        isMonthly,
        starts,
        ends,
        numActiveFilters,
        sortBy,
        event,
        destination,
        city,
        isRebookingReservation,
        isAdmin,
        selectedSpot,
        showNoSpotsFound,
    };
};

const mapDispatchToProps = {
    onResetFiltersClick: resetFilters,
    cancelAllSpotRequests: cancelSpotRequests,
    updateSelectedSpot: updateSelected,
};

const enhance = compose(
    withConfig(['isMobile', 'siteUrl', 'urlDateFormat']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SpotList);
